import React, { useContext, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';
import {
  SBadge,
  SButton,
  SIcon,
  SInput,
  useToast,
  type SIconName,
} from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import {
  PlayContext,
  PlayObjectProperty,
  useDisableDemoReports,
  useShowPlayDemoData,
} from '@bbkAdminComponents/play/play-context';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import {
  PlayPresentation,
  playSlice,
  PlayType,
  type PlayEdit,
} from '@bbkAdminRedux/rtkq/play.slice';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { PublishState } from '@bbkAdminUtils/api-client/rest-offers-api';
import { useParams } from '@bbkShared/react-router';
import { validatePlayName } from '../editor/play-validation-rules';

type PlayNameAndBadgesProps = {
  play: PlayEdit;
  showActivationDate?: boolean;
  leftAction?: React.ReactNode;
  currentStep?: number;
};

export const PlayNameAndBadges: React.FC<PlayNameAndBadgesProps> = ({
  play,
  showActivationDate = false,
  leftAction,
  currentStep,
}) => {
  const { getPlayObjectProperty, setPlayObjectProperty } =
    useContext(PlayContext);
  const appKey = useSelector(selectAppId);
  const { playId } = useParams<{ playId: string }>();
  const { toast } = useToast();
  const [editTitle, setEditTitle] = useState<boolean>(false);
  const [originalTitle, setOriginalTitle] = useState<string>('');
  const [editingName, setEditingName] = useState<string>('');
  const displayNameInputRef = useRef<HTMLInputElement | null>(null);
  const [updatePlay, { isLoading: isPlayUpdating }] =
    playSlice.endpoints.updatePlay.useMutation();
  const [disableDemoReports] = useDisableDemoReports();
  const isDemoData = useShowPlayDemoData({ disableDemoReports });
  const presentation = play?.presentation;
  const brandId = useGetSelectedBrand(appKey) ?? DEFAULT_BRAND_ID;

  // Get plays list for duplicate name checking
  const { data: playsList } = playSlice.endpoints.listPlays.useQuery({
    appKey,
    brandId,
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  });

  // Get play name from PlayContext or play object
  const playTitle =
    (getPlayObjectProperty(PlayObjectProperty.PLAY_TITLE) as string) ||
    play?.name ||
    (originalTitle ?? 'New Play');

  const delayFocus = (): void => {
    setTimeout(() => displayNameInputRef.current?.focus(), 100);
  };

  const getPlayPresentationLabel = () => {
    if (presentation === PlayPresentation.PRICING_PAGE) return 'Pricing Table';
    if (presentation === PlayPresentation.MODAL) return 'Modal';
    if (presentation === PlayPresentation.POP_UP) return 'Pop-up';
    if (presentation === PlayPresentation.BANNER) return 'Banner';
    if (presentation === PlayPresentation.CANCEL_PAGE) return 'Cancel Page';
    return 'Play';
  };

  const getPlayPresentationIcon = () => {
    if (presentation === PlayPresentation.PRICING_PAGE)
      return <SIcon name="grid-3x2" size={12} />;
    if (presentation === PlayPresentation.MODAL)
      return <SIcon name="square-stop" size={12} />;
    if (presentation === PlayPresentation.POP_UP)
      return <SIcon name="vote" size={12} />;
    if (presentation === PlayPresentation.BANNER)
      return <SIcon name="panel-top" size={12} />;
    if (presentation === PlayPresentation.CANCEL_PAGE)
      return <SIcon name="user-x" size={12} />;
    return null;
  };

  const getPendoStepClass = () => {
    const stepNumber = currentStep !== undefined ? currentStep + 1 : '';
    if (presentation === PlayPresentation.CANCEL_PAGE)
      return `cancel-page-step-${stepNumber} ${getPlayTypeLabel()}-step-${stepNumber} ${getPlayTypeLabel()}-${presentation}-step-${stepNumber}`;
    if (
      presentation === PlayPresentation.MODAL ||
      presentation === PlayPresentation.POP_UP ||
      presentation === PlayPresentation.BANNER
    )
      return `in-app-step-${stepNumber} ${presentation}-step-${stepNumber} ${getPlayTypeLabel()}-step-${stepNumber} ${getPlayTypeLabel()}-${presentation}-step-${stepNumber}`;
    if (presentation === PlayPresentation.PRICING_PAGE)
      return `pricing-table-step-${stepNumber} ${getPlayTypeLabel()}-step-${stepNumber} ${getPlayTypeLabel()}-${presentation}-step-${stepNumber}`;
    return '';
  };

  const getPlayTypeBadgeVariant = () => {
    const actualPlayType = play?.play_type;
    if (actualPlayType === PlayType.UPSELL) return 'success';
    if (actualPlayType === PlayType.ACQUISITION) return 'warning';
    if (actualPlayType === PlayType.RETENTION) return 'info';
    return 'neutral';
  };

  const getPlayTypeLabel = () => {
    const actualPlayType = play?.play_type;
    if (actualPlayType === PlayType.ACQUISITION) return 'Acquisition';
    if (actualPlayType === PlayType.UPSELL) return 'Expansion';
    if (actualPlayType === PlayType.RETENTION) return 'Retention';

    return 'Retention';
  };

  const getPlayTypeIcon = (): SIconName => {
    const actualPlayType = play?.play_type;
    if (actualPlayType === PlayType.ACQUISITION) return 'arrow-up-right';
    if (actualPlayType === PlayType.UPSELL) return 'user-plus';
    if (actualPlayType === PlayType.RETENTION) return 'magnet';

    return 'magnet';
  };

  const updatePlayData = (updatedPlay: PlayEdit, onSuccess: () => void) => {
    if (updatedPlay.id && appKey && playId) {
      updatePlay({ appKey, playId, updatedPlay })
        .unwrap()
        .then(onSuccess)
        .catch((e: RTKAPIErrorResponse) => {
          const err = parseRTKAPIError(e);
          BlackToastTrigger({
            content: err.message,
            type: 'error',
          });
        });
    }
  };

  const handleNameSave = () => {
    const realPlaysList = isDemoData ? [] : playsList || [];
    const existingNames = realPlaysList
      .filter((p) => p.id !== playId)
      .map((p) => p.name);

    const nameValidation = validatePlayName(editingName, existingNames);

    if (!nameValidation.isValid) {
      BlackToastTrigger({
        content: nameValidation.errors[0] || 'Enter a valid play title',
        type: 'error',
      });
      return;
    }

    if (originalTitle !== editingName) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TITLE, editingName);

      if (appKey && playId) {
        const updatedPlay = { ...play, name: editingName };
        updatePlayData(updatedPlay, () => {
          BlackToastTrigger({
            content: 'Name updated successfully',
            type: 'success',
          });
        });
      }
    }
    setEditTitle(false);
  };

  const handleEditClick = () => {
    const currentName = playTitle;
    setOriginalTitle(currentName);
    setEditingName(currentName);
    setEditTitle(true);
    delayFocus();
  };

  const handleDiscard = () => {
    // Revert to original name
    setEditingName(originalTitle);
    setPlayObjectProperty(PlayObjectProperty.PLAY_TITLE, originalTitle);
    setEditTitle(false);
  };

  return (
    <div className="tw-flex tw-items-start tw-gap-2">
      {leftAction || <></>}
      <div className="tw-flex tw-flex-col">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <div className="tw-min-w-0 sm:tw-max-w-24 md:tw-max-w-32 lg:tw-max-w-64 xl:tw-max-w-xl">
            {editTitle ? (
              <SInput
                ref={displayNameInputRef}
                className="tw-w-full tw-pr-1"
                placeholder="Play Name"
                testId="default-input"
                value={editingName}
                append={
                  <SButton
                    aria-label="Save"
                    size="small"
                    variant="neutral-ghost"
                    onClick={handleNameSave}
                  >
                    <SIcon name="check" size={16} />
                  </SButton>
                }
                onChange={(e) => {
                  setEditingName(e.currentTarget.value);
                  if (setPlayObjectProperty) {
                    setPlayObjectProperty(
                      PlayObjectProperty.PLAY_TITLE,
                      e.currentTarget.value
                    );
                  }
                }}
                onKeyUp={(e) => {
                  if (e.keyCode === 13) {
                    // Enter key saves
                    handleNameSave();
                  } else if (e.keyCode === 27) {
                    // Escape key discards
                    handleDiscard();
                  }
                }}
              />
            ) : (
              <span className="tw-block tw-truncate tw-text-heading-md tw-text-cbrneutral-900">
                {playTitle}
              </span>
            )}
          </div>
          <SButton
            aria-label={editTitle ? 'Discard' : 'Edit'}
            size="small"
            disabled={isPlayUpdating}
            variant={editTitle ? 'danger' : 'neutral-ghost'}
            onClick={editTitle ? handleDiscard : handleEditClick}
          >
            <SIcon name={editTitle ? 'x' : 'pencil'} size={16} />
          </SButton>
          {play?.play_type && (
            <SBadge
              icon={<SIcon name={getPlayTypeIcon()} />}
              size="regular"
              className="!tw-p-2"
              variant={getPlayTypeBadgeVariant()}
            >
              {getPlayTypeLabel()}
            </SBadge>
          )}
          {play?.presentation && (
            <SBadge
              icon={getPlayPresentationIcon()}
              size="regular"
              className={`!tw-px-2 ${getPendoStepClass()}`}
              variant="neutral"
            >
              {getPlayPresentationLabel()}
            </SBadge>
          )}
          {play?.publish_state === PublishState.draft && (
            <SBadge size="regular" className="!tw-px-2" variant="warning">
              Draft
            </SBadge>
          )}
        </div>
        {showActivationDate && play?.created_at && (
          <span className="tw-pt-1 tw-text-xs tw-leading-5 tw-text-cbrneutral-500">
            Play activated {moment(play.created_at).format('MM/DD/YYYY')}
          </span>
        )}
      </div>
    </div>
  );
};
